import axios, { AxiosError, AxiosResponse } from "axios";
import {
  Button,
  Container,
  Form,
  Input,
  List,
  Stack,
  Text,
  useBasisFormContext,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { EnhancedAccount } from "library/accounts/types";
import { ApiError, ErrorType } from "library/api/address/types.generated";
import { components } from "library/api/card-management-service.generated";
import { postHref } from "library/api/httpMethods";
import { useNav } from "pages/ManagePersonalDetails/hooks/useNav";
import { Dispatch } from "react";
import { UseFormReturn } from "react-hook-form";
import { useMutation } from "react-query";
import { AccountHeader } from "../components/AccountHeader";
import { FullPersonalDetails } from "../types";
import { getFullMobileNumber } from "../UpdateMobileNumber/utils";
import { getCountryCodePrefix, getCreateSessionUrl } from "../utils";
import { Actions, ActionTypes, PhoneNumbersFormValues } from "./types";
import { useIsWorkNumber } from "./useIsWorkNumber";

interface Props {
  account: EnhancedAccount;
  fullPersonalDetails: FullPersonalDetails;
  dispatch: Dispatch<Actions>;
  methods: UseFormReturn<PhoneNumbersFormValues>;
}

export const HomeWorkNumberForm = ({
  account,
  fullPersonalDetails,
  dispatch,
  methods,
}: Props) => {
  const { Field } = useBasisFormContext<PhoneNumbersFormValues>();
  const { workPhone, mobilePhone, homePhone, residential } =
    fullPersonalDetails;
  const residentialCountry = residential.country;
  const countryCodePrefix = getCountryCodePrefix(residentialCountry);
  const { goBack } = useNav();
  const isWork = useIsWorkNumber();
  const token = account.credit_card?.token;

  const updateMutation = useMutation<
    AxiosResponse<components["schemas"]["HomePhoneUpdateResponse"]>,
    AxiosError<ApiError>,
    { values: PhoneNumbersFormValues; token2FaRenewed?: string }
  >(({ values }) =>
    postHref(fullPersonalDetails.updatePhoneEndpoint, {
      home_number: isWork ? homePhone : values.number,
      mobile_number: mobilePhone,
      work_number: isWork ? values.number : workPhone,
    }),
  );

  const handleError = (
    number: string,
    error: AxiosError<ApiError> | unknown,
  ) => {
    if (!axios.isAxiosError(error)) {
      return dispatch({ type: ActionTypes.ERROR });
    }

    const errorType = error.response?.data?.type ?? null;
    const createSessionUrl = getCreateSessionUrl({
      links: error.response?.data.links,
    });

    if (errorType === ErrorType.REQUIRED_2FA && !!createSessionUrl && !!token) {
      return dispatch({
        type: ActionTypes.EXPIRED_OTP,
        payload: { createSessionUrl, token, number },
      });
    }

    return dispatch({ type: ActionTypes.ERROR });
  };

  const onSubmit = async (values: PhoneNumbersFormValues) => {
    if (!token) {
      dispatch({ type: ActionTypes.ERROR });
      return;
    }

    const fullMobileNumber = getFullMobileNumber(
      countryCodePrefix,
      values.number,
    );

    try {
      await updateMutation.mutateAsync({
        values: { number: fullMobileNumber },
      });
      dispatch({ type: ActionTypes.SUCCESS });
      /**
       * 🚨 TS 4.4 Catch Error defaulting to the unknown Type.
       * https://devblogs.microsoft.com/typescript/announcing-typescript-4-4/#use-unknown-catch-variables
       */
    } catch (err: unknown) {
      handleError(values.number, err);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <Stack gap="10">
        <AccountHeader account={account} />
        <Stack gap="4">
          <Text as="h3" textStyle="heading5" color="primary.blue.t100">
            {isWork ? "Work " : "Home "}number
          </Text>
          <Text>{fullPersonalDetails.homePhone}</Text>
        </Stack>
        <List>
          <List.Item>
            <Text textStyle="legal">
              Please provide an updated {isWork ? "work" : "home"} phone number,
              this must be different to your mobile number.
            </Text>
          </List.Item>
          <List.Item>
            <Text textStyle="legal">
              Don't have a specific {isWork ? "work" : "home"} phone number or
              landline? Leave the field empty and press Save. We'll stay in
              touch using the mobile number you provided.
            </Text>
          </List.Item>
        </List>
        <Container width="100%" width-sm="400">
          <Field
            as={Input}
            label={isWork ? "New work number" : "New home number"}
            name="number"
            type="tel"
            prefix={countryCodePrefix}
          />
        </Container>
        <Container width="100%" width-sm="400">
          <FormActions>
            <FormActions.Primary>
              <Button type="submit" loading={updateMutation.isLoading}>
                Save
              </Button>
            </FormActions.Primary>
            <FormActions.Secondary>
              <Button
                variant="secondary"
                disabled={updateMutation.isLoading}
                onClick={goBack}
              >
                Back
              </Button>
            </FormActions.Secondary>
          </FormActions>
        </Container>
      </Stack>
    </Form>
  );
};
